import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

interface Ad {
  id: number;
  title: string;
  description: string;
  price: number;
  price_type: string;
  quantity: number;
  weight: number | null;
  gender: string;
  contact_phone: string;
  city: { name_ar: string } | null;
  category: { id: number; name_ar: string; slug: string } | null;
  subcategory: { name_ar: string } | null;
  images: { image_url: string; thumbnail_url: string | null; is_primary: boolean }[];
  views_count: number;
  favorites_count: number;
  is_featured: boolean;
  created_at: string;
  published_at: string;
  user: { id: number; name: string; avatar: string | null; is_verified: boolean } | null;
  tags: { id: number; name_ar: string }[];
}

interface Category { id: number; name_ar: string; slug: string; type: string; }
interface Service { id: number; name_ar: string; slug: string; icon: string; }
interface Region { id: number; name_ar: string; }
interface City { id: number; name_ar: string; }

type SectionType = 'all' | 'livestock' | 'services';

const SLUG_EMOJI: Record<string, string> = {
  sheep: '🐑', camels: '🐫', goats: '🐐', cattle: '🐄', horses: '🐴', birds: '🐦',
};
const SERVICE_EMOJI: Record<string, string> = {
  'transport-shipping': '🚛', 'veterinary-services': '🩺', 'feed-nutrition': '🌾', 'pens-fencing': '🏗', consulting: '💬',
};

const AdsPage: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [section, setSection] = useState<SectionType>('all');
  const [categoryId, setCategoryId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [cityId, setCityId] = useState('');
  const [sortBy, setSortBy] = useState('latest');

  useEffect(() => {
    const init = async () => {
      try {
        const [catRes, srvRes, regRes] = await Promise.all([
          api.get('/categories'),
          api.get('/services'),
          api.get('/regions'),
        ]);
        setCategories(catRes.data.data || []);
        setServices(srvRes.data.data || []);
        setRegions(regRes.data.data || []);
      } catch (err) { console.error(err); }
    };
    init();
  }, []);

  useEffect(() => {
    if (regionId) {
      api.get('/regions/' + regionId + '/cities').then(r => setCities(r.data.data || [])).catch(() => setCities([]));
    } else { setCities([]); setCityId(''); }
  }, [regionId]);

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, regionId, cityId, sortBy, currentPage, section]);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage };
      if (categoryId) params.category_id = categoryId;
      if (regionId) params.region_id = regionId;
      if (cityId) params.city_id = cityId;
      if (sortBy) params.sort = sortBy;
      const res = await api.get('/ads', { params });
      const data = res.data.data;
      setAds(data?.ads || []);
      setCurrentPage(data?.pagination?.current_page || 1);
      setLastPage(data?.pagination?.last_page || 1);
      setTotal(data?.pagination?.total || 0);
    } catch (err) { console.error(err); setAds([]); }
    finally { setLoading(false); }
  };

  const timeAgo = (dateStr: string) => {
    const diff = Math.floor((new Date().getTime() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return 'الآن';
    if (diff < 3600) return 'منذ ' + Math.floor(diff / 60) + ' دقيقة';
    if (diff < 86400) return 'منذ ' + Math.floor(diff / 3600) + ' ساعة';
    if (diff < 2592000) return 'منذ ' + Math.floor(diff / 86400) + ' يوم';
    return 'منذ ' + Math.floor(diff / 2592000) + ' شهر';
  };

  const genderMap: Record<string, string> = { male: 'ذكر', female: 'أنثى', mixed: 'مختلط' };

  const handleSection = (s: SectionType) => {
    setSection(s);
    setCategoryId('');
    setCurrentPage(1);
  };

  const handleSubFilter = (id: string) => {
    setCategoryId(id);
    setCurrentPage(1);
  };

  const subFilters = section === 'livestock' ? categories.filter(c => c.type === 'livestock')
    : section === 'services' ? services.map(s => ({ id: s.id, name_ar: s.name_ar, slug: s.slug, type: 'service' }))
    : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');
        .alp{max-width:1200px;margin:0 auto;padding:0 20px;font-family:'Cairo',sans-serif}
        .alp *{box-sizing:border-box}

        /* Section Tabs */
        .alp-sections{padding:20px 0 0;display:flex;gap:0;border-bottom:2px solid #E0E0E0}
        .alp-sec-tab{padding:12px 28px;font-size:15px;font-weight:700;color:#757575;background:none;border:none;border-bottom:3px solid transparent;cursor:pointer;font-family:'Cairo',sans-serif;transition:all 0.2s;margin-bottom:-2px}
        .alp-sec-tab:hover{color:#424242}
        .alp-sec-tab.active{color:#1976D2;border-bottom-color:#1976D2}

        /* Sub filters */
        .alp-subs{padding:14px 0 0;display:flex;gap:8px;flex-wrap:wrap}
        .alp-sub{display:flex;align-items:center;gap:6px;padding:7px 18px;border-radius:9999px;border:1.5px solid #E0E0E0;background:#fff;cursor:pointer;font-family:'Cairo',sans-serif;font-size:13px;font-weight:600;color:#616161;transition:all 0.2s}
        .alp-sub:hover{border-color:#BBDEFB;background:#E3F2FD;color:#1976D2}
        .alp-sub.active{background:#1976D2;border-color:#1976D2;color:#fff}

        /* Filters bar */
        .alp-filters{background:#fff;border-radius:10px;padding:12px 18px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;border:1px solid #E0E0E0;margin:16px 0}
        .alp-flabel{font-size:13px;font-weight:700;color:#424242;white-space:nowrap}
        .alp-fselect{appearance:none;padding:8px 32px 8px 14px;border:1.5px solid #E0E0E0;border-radius:8px;font-family:'Cairo',sans-serif;font-size:13px;font-weight:500;color:#424242;background:#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23757575' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat left 12px center;cursor:pointer;outline:none;min-width:120px}
        .alp-fselect:focus{border-color:#1976D2;box-shadow:0 0 0 3px rgba(25,118,210,0.1)}
        .alp-fdiv{width:1px;height:24px;background:#E0E0E0;margin:0 4px}
        .alp-rcount{font-size:12px;color:#757575;font-weight:500;margin-right:auto;font-family:'Inter','Cairo',sans-serif}
        .alp-rcount strong{color:#1976D2;font-weight:700}
        .alp-clear{background:none;border:none;color:#F44336;font-size:12px;font-weight:600;cursor:pointer;font-family:'Cairo',sans-serif;padding:6px 10px}

        /* Cards */
        .alp-grid{display:flex;flex-direction:column;gap:10px;padding-bottom:30px}
        .alp-card{background:#fff;border-radius:10px;border:1px solid #E0E0E0;overflow:hidden;display:flex;position:relative;cursor:pointer;transition:all 0.2s;text-decoration:none;color:inherit}
        .alp-card:hover{box-shadow:0 4px 15px rgba(0,0,0,0.07);transform:translateY(-1px)}
        .alp-card-img{width:180px;height:150px;position:relative;flex-shrink:0;overflow:hidden}
        .alp-card-img img{width:100%;height:100%;object-fit:cover;transition:transform 0.3s}
        .alp-card:hover .alp-card-img img{transform:scale(1.04)}
        .alp-card-img-empty{width:100%;height:100%;background:linear-gradient(135deg,#E3F2FD,#BBDEFB);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;color:#1976D2}
        .alp-card-img-empty span{font-size:11px;font-weight:500}
        .alp-badge-img{position:absolute;top:8px;right:8px;padding:3px 10px;border-radius:9999px;font-size:10px;font-weight:700;backdrop-filter:blur(8px)}
        .alp-badge-feat{background:rgba(255,152,0,0.9);color:#fff}
        .alp-fav-btn{position:absolute;top:8px;left:8px;width:30px;height:30px;background:rgba(255,255,255,0.9);backdrop-filter:blur(8px);border:none;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#BDBDBD;font-size:15px;transition:all 0.2s}
        .alp-fav-btn:hover{color:#F44336;transform:scale(1.1)}

        /* Card body */
        .alp-card-body{flex:1;padding:14px 18px;display:flex;flex-direction:column;justify-content:space-between;min-width:0}
        .alp-card-top{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:6px}
        .alp-card-title{font-size:15px;font-weight:700;color:#212121;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1}
        .alp-card-price{font-size:18px;font-weight:800;color:#4CAF50;white-space:nowrap;flex-shrink:0;font-family:'Inter','Cairo',sans-serif}
        .alp-card-price small{font-size:12px;font-weight:600;color:#757575;font-family:'Cairo',sans-serif}
        .alp-card-meta{display:flex;align-items:center;gap:14px;margin-bottom:8px;flex-wrap:wrap}
        .alp-meta-item{display:flex;align-items:center;gap:4px;font-size:12px;color:#757575;font-weight:500}
        .alp-meta-loc{color:#F44336}
        .alp-card-tags{display:flex;gap:6px;flex-wrap:wrap}
        .alp-tag{padding:2px 10px;background:#F5F5F5;border-radius:9999px;font-size:11px;font-weight:600;color:#616161}

        /* Card bottom */
        .alp-card-bottom{display:flex;align-items:center;justify-content:space-between;padding-top:10px;border-top:1px solid #F5F5F5;margin-top:auto}
        .alp-seller{display:flex;align-items:center;gap:8px}
        .alp-seller-av{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;color:#fff;background:#1976D2;overflow:hidden;flex-shrink:0}
        .alp-seller-av img{width:100%;height:100%;object-fit:cover}
        .alp-seller-name{font-size:12px;font-weight:600;color:#424242}
        .alp-seller-v{color:#4CAF50;font-size:10px;font-weight:700}
        .alp-actions{display:flex;gap:6px}
        .alp-act-btn{width:30px;height:30px;border:1.5px solid #E0E0E0;background:#fff;border-radius:8px;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#757575;font-size:13px;transition:all 0.2s;text-decoration:none}
        .alp-act-btn:hover{border-color:#1976D2;color:#1976D2;background:#E3F2FD}
        .alp-act-btn.wa{border-color:#25d366;color:#25d366}
        .alp-act-btn.wa:hover{background:#25d366;color:#fff}

        /* Pagination */
        .alp-pagination{display:flex;justify-content:center;align-items:center;gap:6px;margin:20px 0 30px}
        .alp-pg{width:36px;height:36px;border:1.5px solid #E0E0E0;background:#fff;border-radius:8px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:'Inter','Cairo',sans-serif;font-size:13px;font-weight:600;color:#616161;transition:all 0.2s}
        .alp-pg:hover{border-color:#1976D2;color:#1976D2}
        .alp-pg:disabled{opacity:0.4;cursor:default}
        .alp-pg.active{background:#1976D2;border-color:#1976D2;color:#fff}

        .alp-empty{background:#fff;border-radius:10px;border:1px solid #E0E0E0;padding:60px 20px;text-align:center}
        .alp-empty p{color:#9E9E9E;font-size:16px;margin:0 0 8px}
        .alp-empty small{color:#BDBDBD;font-size:13px}
        .alp-loading{text-align:center;padding:60px 20px;color:#9E9E9E;font-size:14px}

        @keyframes alpFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .alp-card{animation:alpFade 0.3s ease forwards}

        @media(max-width:640px){
          .alp-card{flex-direction:column}
          .alp-card-img{width:100%;height:180px}
          .alp-card-top{flex-direction:column;gap:4px}
          .alp-subs{overflow-x:auto;flex-wrap:nowrap;padding-bottom:4px}
          .alp-sec-tab{padding:10px 18px;font-size:14px}
          .alp-fselect{min-width:100px;flex:1}
        }
      `}</style>

      <div className="alp">
        {/* Section Tabs: الكل | مواشي | خدمات */}
        <div className="alp-sections">
          <button className={'alp-sec-tab' + (section === 'all' ? ' active' : '')} onClick={() => handleSection('all')}>
            الكل
          </button>
          <button className={'alp-sec-tab' + (section === 'livestock' ? ' active' : '')} onClick={() => handleSection('livestock')}>
            🐑 مواشي
          </button>
          <button className={'alp-sec-tab' + (section === 'services' ? ' active' : '')} onClick={() => handleSection('services')}>
            🔧 خدمات
          </button>
        </div>

        {/* Sub-category filters */}
        {subFilters.length > 0 && (
          <div className="alp-subs">
            <button className={'alp-sub' + (!categoryId ? ' active' : '')} onClick={() => handleSubFilter('')}>
              الكل
            </button>
            {subFilters.map(sf => (
              <button
                key={sf.id}
                className={'alp-sub' + (categoryId === String(sf.id) ? ' active' : '')}
                onClick={() => handleSubFilter(String(sf.id))}
              >
                {section === 'livestock' ? (SLUG_EMOJI[sf.slug] || '📦') : (SERVICE_EMOJI[sf.slug] || '🔧')} {sf.name_ar}
              </button>
            ))}
          </div>
        )}

        {/* Filters bar */}
        <div className="alp-filters">
          <span className="alp-flabel">⚙ تصفية:</span>

          <select className="alp-fselect" value={regionId} onChange={(e) => { setRegionId(e.target.value); setCityId(''); setCurrentPage(1); }}>
            <option value="">كل المناطق</option>
            {regions.map(r => <option key={r.id} value={r.id}>{r.name_ar}</option>)}
          </select>

          {cities.length > 0 && (
            <select className="alp-fselect" value={cityId} onChange={(e) => { setCityId(e.target.value); setCurrentPage(1); }}>
              <option value="">كل المدن</option>
              {cities.map(c => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
            </select>
          )}

          <select className="alp-fselect" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}>
            <option value="latest">الأحدث</option>
            <option value="price_asc">الأقل سعراً</option>
            <option value="price_desc">الأعلى سعراً</option>
            <option value="popular">الأكثر مشاهدة</option>
          </select>

          <div className="alp-fdiv"></div>
          <span className="alp-rcount">عرض <strong>{total}</strong> نتيجة</span>

          {(categoryId || regionId || cityId) && (
            <button className="alp-clear" onClick={() => { setCategoryId(''); setRegionId(''); setCityId(''); setSortBy('latest'); setCurrentPage(1); setSection('all'); }}>✕ مسح</button>
          )}
        </div>

        {/* Ads List */}
        {loading ? (
          <div className="alp-loading">جاري التحميل...</div>
        ) : ads.length === 0 ? (
          <div className="alp-empty">
            <p>لا توجد إعلانات</p>
            <small>جرّب تغيير الفلاتر</small>
          </div>
        ) : (
          <div className="alp-grid">
            {ads.map((ad, idx) => (
              <Link key={ad.id} to={'/ads/' + ad.id} className="alp-card" style={{ animationDelay: (idx * 0.04) + 's' }}>
                <div className="alp-card-img">
                  {ad.images && ad.images.length > 0 ? (
                    <img src={ad.images[0].thumbnail_url || ad.images[0].image_url} alt={ad.title} />
                  ) : (
                    <div className="alp-card-img-empty">
                      <span style={{ fontSize: 28 }}>📷</span>
                      <span>بدون صورة</span>
                    </div>
                  )}
                  {ad.is_featured && <span className="alp-badge-img alp-badge-feat">⭐ مميز</span>}
                  <button className="alp-fav-btn" onClick={(e) => e.preventDefault()}>♡</button>
                </div>
                <div className="alp-card-body">
                  <div>
                    <div className="alp-card-top">
                      <span className="alp-card-title">{ad.title}</span>
                      <div className="alp-card-price">{Number(ad.price).toLocaleString()} <small>ر.س</small></div>
                    </div>
                    <div className="alp-card-meta">
                      {ad.category && <span className="alp-meta-item">📂 {ad.category.name_ar}</span>}
                      {ad.city && <span className="alp-meta-item"><span className="alp-meta-loc">📍</span> {ad.city.name_ar}</span>}
                      <span className="alp-meta-item">🕐 {timeAgo(ad.published_at || ad.created_at)}</span>
                      <span className="alp-meta-item">👁 {ad.views_count || 0}</span>
                    </div>
                    <div className="alp-card-tags">
                      {ad.subcategory && <span className="alp-tag">{ad.subcategory.name_ar}</span>}
                      {ad.gender && <span className="alp-tag">{genderMap[ad.gender] || ad.gender}</span>}
                      {ad.quantity && ad.quantity > 1 && <span className="alp-tag">{ad.quantity} رأس</span>}
                      {ad.weight && <span className="alp-tag">{ad.weight} كجم</span>}
                      {ad.tags && ad.tags.map(t => <span key={t.id} className="alp-tag">{t.name_ar}</span>)}
                    </div>
                  </div>
                  <div className="alp-card-bottom">
                    <div className="alp-seller">
                      <div className="alp-seller-av">
                        {ad.user?.avatar ? <img src={ad.user.avatar} alt="" /> : (ad.user?.name?.charAt(0) || '؟')}
                      </div>
                      <div>
                        <div className="alp-seller-name">{ad.user?.name || 'مستخدم'}</div>
                        {ad.user?.is_verified ? <div className="alp-seller-v">✓ موثّق</div> : <div style={{ fontSize: 10, color: '#9E9E9E' }}>فرد</div>}
                      </div>
                    </div>
                    <div className="alp-actions" onClick={(e) => e.preventDefault()}>
                      {ad.contact_phone && (
                        <a href={'https://wa.me/966' + ad.contact_phone.replace(/^0/, '')} className="alp-act-btn wa" onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" title="واتساب">💬</a>
                      )}
                      {ad.contact_phone && (
                        <a href={'tel:' + ad.contact_phone} className="alp-act-btn" onClick={(e) => e.stopPropagation()} title="اتصال">📞</a>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="alp-pagination">
            <button className="alp-pg" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>→</button>
            {Array.from({ length: Math.min(lastPage, 5) }, (_, i) => i + 1).map(p => (
              <button key={p} className={'alp-pg' + (currentPage === p ? ' active' : '')} onClick={() => setCurrentPage(p)}>{p}</button>
            ))}
            {lastPage > 5 && <span style={{ color: '#9E9E9E', fontSize: 13 }}>...</span>}
            {lastPage > 5 && <button className={'alp-pg' + (currentPage === lastPage ? ' active' : '')} onClick={() => setCurrentPage(lastPage)}>{lastPage}</button>}
            <button className="alp-pg" onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))} disabled={currentPage === lastPage}>←</button>
          </div>
        )}
      </div>
    </>
  );
};

export default AdsPage;
